import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from 'src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { PlusIcon, PencilIcon, TrashIcon } from '@phosphor-icons/react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import config from 'src/config';
import { CompanyDTO } from 'src/api/core/companies/useCompany';

interface Address {
  id: string;
  recipientName?: string | null;
  addressLine1: string;
  addressLine2?: string | null;
  locality: string;
  administrativeArea?: string | null;
  postalCode?: string | null;
  countryIso31661Alpha2: string;
}

interface CreateAddressData {
  recipientName?: string;
  addressLine1: string;
  addressLine2?: string;
  locality: string;
  administrativeArea?: string;
  postalCode?: string;
  countryIso31661Alpha2: string;
}

interface CompanyAddressesTabProps {
  company: CompanyDTO;
  onRefresh?: () => void;
}

export function CompanyAddressesTab({
  company,
  onRefresh,
}: CompanyAddressesTabProps) {
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newAddress, setNewAddress] = useState<CreateAddressData>({
    addressLine1: '',
    locality: '',
    countryIso31661Alpha2: 'US',
  });

  // For now, we'll show the primary address if it exists
  // TODO: Implement full address management API for companies
  const addresses: Address[] = company.primaryAddress
    ? [
        {
          id: company.primaryAddress.id,
          recipientName: null, // Primary address doesn't have recipient name
          addressLine1: company.primaryAddress.addressLine1,
          addressLine2: company.primaryAddress.addressLine2,
          locality: company.primaryAddress.locality,
          administrativeArea: company.primaryAddress.administrativeArea,
          postalCode: company.primaryAddress.postalCode,
          countryIso31661Alpha2: company.primaryAddress.countryIso31661Alpha2,
        },
      ]
    : [];

  const handleCreateOrUpdateAddress = async () => {
    try {
      setIsLoading(true);
      if (editingAddress) {
        // Update existing address
        const response = await fetch(
          `${config.apiBaseUrl}/core/companies/${company.id}/addresses/${editingAddress.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newAddress),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to update address');
        }
      } else {
        // Create new address
        const response = await fetch(
          `${config.apiBaseUrl}/core/companies/${company.id}/addresses`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newAddress),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to create address');
        }
      }

      onRefresh?.();
      handleModalClose();
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setNewAddress({
      recipientName: address.recipientName || '',
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      locality: address.locality,
      administrativeArea: address.administrativeArea || '',
      postalCode: address.postalCode || '',
      countryIso31661Alpha2: address.countryIso31661Alpha2,
    });
    setAddressModalOpen(true);
  };

  const handleDeleteAddress = (address: Address) => {
    setAddressToDelete(address);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/core/companies/${company.id}/addresses/${addressToDelete.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to delete address');
      }

      onRefresh?.();
      setDeleteConfirmOpen(false);
      setAddressToDelete(null);
    } catch (error) {
      console.error('Error deleting address:', error);
      setDeleteConfirmOpen(false);
      setAddressToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setAddressModalOpen(false);
    setEditingAddress(null);
    setNewAddress({
      addressLine1: '',
      locality: '',
      countryIso31661Alpha2: 'US',
    });
  };

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium">Addresses</h2>
          <Button
            onClick={() => setAddressModalOpen(true)}
            size="sm"
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Address
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Recipient
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Address Line 1
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Address Line 2
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">City</span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">State</span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Postal Code
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Country
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Actions
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {addresses.map((address) => (
              <TableRow key={address.id}>
                <TableCell>
                  <span className="text-sm">
                    {address.recipientName || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{address.addressLine1}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{address.addressLine2 || '-'}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{address.locality}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {address.administrativeArea || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{address.postalCode || '-'}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {address.countryIso31661Alpha2}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAddress(address)}
                      className="h-8 w-8 p-0"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAddress(address)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {addresses.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className="text-center py-4">
                    <span className="text-sm text-gray-600">
                      No addresses found for this company
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Address Creation/Edit Modal */}
      <Dialog open={addressModalOpen} onOpenChange={handleModalClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </DialogTitle>
            <DialogDescription>
              {editingAddress
                ? 'Update the address for this company.'
                : 'Add a new address for this company.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="recipientName">Recipient Name (Optional)</Label>
              <input
                id="recipientName"
                type="text"
                value={newAddress.recipientName || ''}
                onChange={(e) =>
                  setNewAddress({
                    ...newAddress,
                    recipientName: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter recipient name"
              />
            </div>
            <div>
              <Label htmlFor="addressLine1">Address Line 1 *</Label>
              <input
                id="addressLine1"
                type="text"
                value={newAddress.addressLine1}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, addressLine1: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter street address"
                required
              />
            </div>
            <div>
              <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
              <input
                id="addressLine2"
                type="text"
                value={newAddress.addressLine2 || ''}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, addressLine2: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Apartment, suite, etc."
              />
            </div>
            <div>
              <Label htmlFor="locality">City *</Label>
              <input
                id="locality"
                type="text"
                value={newAddress.locality}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, locality: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter city"
                required
              />
            </div>
            <div>
              <Label htmlFor="administrativeArea">
                State/Province (Optional)
              </Label>
              <input
                id="administrativeArea"
                type="text"
                value={newAddress.administrativeArea || ''}
                onChange={(e) =>
                  setNewAddress({
                    ...newAddress,
                    administrativeArea: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter state or province"
              />
            </div>
            <div>
              <Label htmlFor="postalCode">Postal Code (Optional)</Label>
              <input
                id="postalCode"
                type="text"
                value={newAddress.postalCode || ''}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, postalCode: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter postal code"
              />
            </div>
            <div>
              <Label htmlFor="countryIso31661Alpha2">Country *</Label>
              <select
                id="countryIso31661Alpha2"
                value={newAddress.countryIso31661Alpha2}
                onChange={(e) =>
                  setNewAddress({
                    ...newAddress,
                    countryIso31661Alpha2: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="MX">Mexico</option>
                <option value="GB">United Kingdom</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="IT">Italy</option>
                <option value="ES">Spain</option>
                <option value="AU">Australia</option>
                <option value="JP">Japan</option>
                <option value="CN">China</option>
                <option value="IN">India</option>
                <option value="BR">Brazil</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdateAddress}
              disabled={
                !newAddress.addressLine1 ||
                !newAddress.locality ||
                !newAddress.countryIso31661Alpha2 ||
                isLoading
              }
            >
              {isLoading
                ? 'Saving...'
                : editingAddress
                  ? 'Update Address'
                  : 'Add Address'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteAddress}
        title="Delete Address"
        message={`Are you sure you want to delete this address? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        dangerous={true}
      />
    </>
  );
}
